'use client'

import { useMemo, useState, useCallback, useEffect } from 'react'
import { AirlineDetail } from './airlineDetail'
import type { AirlinePage } from '@/src/types/airlineDetail'
import topAirlinesData from '@/src/constants/topAirlines.json'
import { useTranslations } from 'next-intl'

interface AirlineDetailWrapperProps {
    airlineData: AirlinePage | null
    locale: string
    isMobile: boolean
}

const AirlineDetailWrapper: React.FC<AirlineDetailWrapperProps> = ({ airlineData, locale, isMobile }) => {

    const t = useTranslations('airlinePages')
    const [activeTab, setActiveTab] = useState<string>('')
    const [showAllRoutes, setShowAllRoutes] = useState<boolean>(false)

    const isArabic = locale === 'ar'
    const airlines = useMemo(
        () =>
            topAirlinesData.airlines.map(airline => ({
                ...airline,
                name: isArabic ? airline.name_ar || airline.name_en : airline.name_en,
                href: isArabic ? airline.href_ar || airline.href_en : airline.href_en
            })),
        [topAirlinesData.airlines, isArabic]
    )

    // Error handling
    if (!airlineData || !airlineData.airlineData) {
        return (
            <AirlineDetail
                isError={true}
                errorMessage="Airline information not available"
                isMobile={isMobile}
                t={t}
            />
        )
    }

    const {
        airlineData: data,
        longName,
        aboutAirline,
        frequentFlightRoutes,
        airlineNews,
        airlineHighlights,
        generalInformation,
        popularAirlineDestinations,
        faq,
        historicMilestones,
        popularAirlines,
        awards,
        bookingTable
    } = airlineData

    // Extract hero data
    const hero = data.hero
    const code = data.code

    // Initialize active tab from highlights (first tab key if available)
    useEffect(() => {
        if (!activeTab && airlineHighlights?.tabs && airlineHighlights.tabs.length > 0) {
            setActiveTab(airlineHighlights.tabs[0].key)
        }
    }, [])

    // Event handlers
    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab)
    }, [])

    const handleToggleRoutes = useCallback(() => {
        setShowAllRoutes(prev => !prev)
    }, [])

    type FlightRoute = {
        url: string;
      };
      
    function generateFlightScheduleLink(
        text: string
      ) {
        /**
         * Expected formats:
         * "Flights from Riyadh to Jeddah"
         * "Flight from Riyadh to Jeddah"
         */
        const match = text.match(/from\s+(.+?)\s+to\s+(.+)/i);
      
        if (!match) return null;
      
        const from = match[1].trim();
        const to = match[2].trim();
      
        const slugify = (value: string) =>
          value
            .toLowerCase()
            .replace(/[^a-z\s]/g, '')
            .trim()
            .replace(/\s+/g, '-');
      
        const fromSlug = slugify(from);
        const toSlug = slugify(to);
      
        const url = `/flight-schedule/${fromSlug}-${toSlug}-flights.html`;
      
        return {
          url
        };
      }
      

    return (
        <AirlineDetail
            isError={false}
            hero={hero}
            code={code}
            longName={longName}
            aboutAirline={aboutAirline}
            frequentFlightRoutes={frequentFlightRoutes}
            airlineNews={airlineNews}
            airlineBooking={bookingTable}
            airlineAwards={awards}
            airlineHighlights={airlineHighlights}
            generalInformation={generalInformation}
            popularAirlineDestinations={popularAirlineDestinations}
            faq={faq}
            historicMilestones={historicMilestones}
            popularAirlines={popularAirlines}
            activeTab={activeTab}
            showAllRoutes={showAllRoutes}
            onTabChange={handleTabChange}
            onToggleRoutes={handleToggleRoutes}
            topAirlines={airlines}
            isMobile={isMobile}
            generateFlightScheduleLink={generateFlightScheduleLink}
            t={t}
        />
    )
}

export default AirlineDetailWrapper
